import { isNativePlatform } from '../utils/platform';

export type CameraSourceOption = 'camera' | 'photos';

export interface NativeCameraResult {
  file: File;
  dataUrl: string;
}

/**
 * Returns a function that opens the native camera/photo library on mobile,
 * or falls back to a hidden <input type="file"> on web.
 */
export function useNativeCamera() {
  async function pickPhoto(source: CameraSourceOption = 'photos'): Promise<NativeCameraResult | null> {
    if (!isNativePlatform()) return null;

    const { Camera, CameraSource, CameraResultType } = await import('@capacitor/camera');

    const nativeSource = source === 'camera' ? CameraSource.Camera : CameraSource.Photos;

    let photo;
    try {
      photo = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: nativeSource,
      });
    } catch (err: any) {
      // User cancelled or permission denied — return null silently
      if (
        err?.message?.includes('cancelled') ||
        err?.message?.includes('canceled') ||
        err?.message?.includes('User denied')
      ) {
        return null;
      }
      throw err;
    }

    if (!photo.dataUrl) return null;

    // Convert data URL to File
    const res = await fetch(photo.dataUrl);
    const blob = await res.blob();
    const extension = photo.format || 'jpeg';
    const file = new File([blob], `photo.${extension}`, { type: blob.type || `image/${extension}` });

    return { file, dataUrl: photo.dataUrl };
  }

  return { pickPhoto };
}
